import SaveIcon from '@mui/icons-material/Save';

import React, { useContext, useState } from 'react';

import { ApiContext } from '_lib/api/apiContext';
import { BadRequestError } from '_lib/api/apiErrors';
import { AppConfigDetailDTO, FieldIssue } from '_lib/api/apiTypes.shim';
import { BackdropSpinner } from '_lib/components/BackdropSpinner/BackdropSpinner';
import { Button } from '_lib/components/Button/Button';
import { Divider } from '_lib/components/Divider/Divider';
import { ErrorDisplay } from '_lib/components/ErrorDisplay/ErrorDisplay';
import { FieldSet } from '_lib/components/FieldSet/FieldSet';
import { FlexBox } from '_lib/components/FlexBox/FlexBox';
import { Stack } from '_lib/components/Stack/Stack';
import { TextField } from '_lib/components/TextField/TextField';
import { useOurSnackbar } from '_lib/utils/snackUtils';

type ServiceTitanConfigManagementProps = {
  appConfigDetail: AppConfigDetailDTO;
};

export const ServiceTitanConfigManagement = (props: ServiceTitanConfigManagementProps) => {
  const { appConfigDetail } = props;

  const [serviceTitanConfigDraft, setServiceTitanConfigDraft] = useState<AppConfigDetailDTO['serviceTitanConfig']>(
    appConfigDetail.serviceTitanConfig
  );
  const [fieldIssues, setFieldIssues] = useState<FieldIssue[]>([]);
  const [nonFieldErrors, setNonFieldErrors] = useState<string[]>([]);

  const { enqueueSnackbar } = useOurSnackbar();

  const api = useContext(ApiContext);

  const appConfigUpdateMutation = api.appConfig.useUpdateAppConfig();

  return (
    <FlexBox
      sx={{
        flexDirection: 'column',
        position: 'relative'
      }}>
      <BackdropSpinner open={appConfigUpdateMutation.isPending} />
      <FieldSet legendText='Service Titan Config' sx={{ margin: 2, padding: 2 }}>
        <Stack useFlexGap spacing={1} sx={{ width: '100%' }}>
          <TextField
            required
            fullWidth
            label='Service Titan Portal URL'
            value={serviceTitanConfigDraft?.serviceTitanPortalUrl}
            onChange={(e) =>
              setServiceTitanConfigDraft((prev) => (prev ? { ...prev, serviceTitanPortalUrl: e.target.value } : null))
            }
            error={{
              issuePath: 'serviceTitanPortalUrl',
              issues: fieldIssues
            }}
          />
          <TextField
            required
            fullWidth
            label='Service Titan Enterprise URL'
            value={serviceTitanConfigDraft?.serviceTitanEnterpriseUrl}
            onChange={(e) =>
              setServiceTitanConfigDraft((prev) =>
                prev ? { ...prev, serviceTitanEnterpriseUrl: e.target.value } : null
              )
            }
            error={{
              issuePath: 'serviceTitanEnterpriseUrl',
              issues: fieldIssues
            }}
          />
        </Stack>
        {nonFieldErrors.length > 0 && <ErrorDisplay errors={nonFieldErrors} sx={{ borderRadius: 1 }} />}
      </FieldSet>
      <Divider />
      <FlexBox sx={{ width: '100%', height: '48px' }}>
        <Button
          startIcon={<SaveIcon />}
          color='primary'
          disabled={appConfigUpdateMutation.isPending}
          text='Save'
          variant='contained'
          sx={{ textTransform: 'none', marginLeft: 'auto', marginY: 1, marginRight: 1 }}
          onClick={async () => {
            if (!serviceTitanConfigDraft) return;
            try {
              await appConfigUpdateMutation.put({ data: serviceTitanConfigDraft, url: 'service-titan-config' });
              setFieldIssues([]);
              setNonFieldErrors([]);
            } catch (err) {
              if (err instanceof BadRequestError) {
                setFieldIssues(
                  (err.responseData.fieldIssues ?? []).filter((issue) => issue.path?.[0] !== 'nonFieldErrors')
                );
                setNonFieldErrors(err.responseData.nonFieldErrors);
              }
              enqueueSnackbar('Error updating Service Titan Config');
            }
          }}
        />
      </FlexBox>
    </FlexBox>
  );
};
